import { Component } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ApiService } from '../../../../core/services/api.service';
import { ApiEndPoints, ApiRoot } from '../../../../shared/constants/api-endpoints';
import { LookupKey } from '../../../../shared/constants/lookup-keys';
import { CompanyProfileRequest } from '../../../../shared/models/services-request/company-profile.model';
import { SHARED_COMPONENTS } from '../../../../shared/shared-imports';
import { RoutesPath } from '../../../../shared/constants/routes-path';
import { SharedService } from '../../../../shared/services/shared.service';
import { OptionTranslateService } from '../../../../shared/services/option-translate.service';
import { Utils } from '../../../../Utils/utils';
import { FieldValue } from '../../../../shared/constants/services.enum';
import { customValidator } from '../../../../Utils/validator';

@Component({
  selector: 'app-company-additional-information',
  imports: [ReactiveFormsModule, TranslateModule, ...SHARED_COMPONENTS],
  templateUrl: './company-additional-information.component.html',
  styleUrl: './company-additional-information.component.scss'
})
export class CompanyAdditionalInformationComponent {
  form: FormGroup;
  lookupKey = LookupKey;
  lang: string = 'en';
  userLoginName: string = "";
  memberCSN: string = "";
  standardOption: any = [];
  requestPayload: CompanyProfileRequest = new CompanyProfileRequest();
  showOtherIndustry: boolean = false;
  primaryAccountId: string = "";
  constructor(
    private fb: FormBuilder,
    private sharedService: SharedService,
    private optionTranslateService: OptionTranslateService,
    private api: ApiService,
    private router: Router
  ) {

    this.lang = this.sharedService.getCurrentLang();
    this.userLoginName = this.sharedService.getUserProfile()['Login Name'];
    this.memberCSN = this.sharedService.getCsnAccountId('csn');
    this.primaryAccountId = this.sharedService.getCsnAccountId('id');
    this.standardOption = this.optionTranslateService.getOptions(
      'standardOption',
      this.lang
    );

    this.form = this.fb.group({
      companyName: ['', [Validators.required, Validators.maxLength(250), customValidator('safeText')]],
      industry: ['', [Validators.required]],
      otherIndustry: ['', [Validators.maxLength(100), customValidator('safeText')]],
      employees: ['', [Validators.required]],
      websiteURL: ['', [Validators.maxLength(100), customValidator('websiteLink')]],
      linkedInURL: ['', [Validators.maxLength(100), customValidator('websiteLink')]],
      otherURL: ['', [Validators.maxLength(100), customValidator('websiteLink')]],
      businessPriorities: ['', [Validators.required]],
      communicationChannel: ['', [Validators.required]],
      industrySurveys: ['', [Validators.required]],
      receivePromotions: ['', [Validators.required]]
    });

    this.form.get('industry')?.valueChanges.subscribe((value) => {
      this.updateOtherIndustryRequirement(value);
    });
    this.updateOtherIndustryRequirement(this.form.get('industry')?.value);
    this.getCompanyDetail();
  }

  private updateOtherIndustryRequirement(value: any): void {
    const isOther = this.isOtherIndustry(value);
    this.showOtherIndustry = isOther;
    Utils.addRemoveValidation(this.form, isOther, ['otherIndustry']);
  }

  private isOtherIndustry(value: any): boolean {
    if (value == null) {
      return false;
    }
    const raw =
      typeof value === 'string'
        ? value
        : value?.Value ?? value?.Name ?? value?.label ?? '';

    const normalized = String(raw).toLowerCase();
    return (
      normalized.includes('other') ||
      normalized.includes('أخرى (يرجى التحديد)') ||
      normalized.includes('أخرى')
    );
  }

  private getYesNoFlag(value: any): string {
    if (value == null || value == "") {
      return '';
    }
    let flagValue = "";
    if (value == FieldValue.YES || value == FieldValue.YES_AR) {
      flagValue = "Y"
    } else if (value == FieldValue.NO || value == FieldValue.NO_AR) {
      flagValue = "N"
    }
    return flagValue;
  }

  private getYesNoValueFromFlag(flag: any): string | null {
    if (flag == null || flag == "") {
      return "";
    }
    const normalized = String(flag).trim().toUpperCase();
    const isYes = normalized === 'Y';
    const isNo = normalized === 'N';
    if (!isYes && !isNo) {
      return "";
    }
    if (this.lang === 'ar') {
      return isYes ? FieldValue.YES_AR : FieldValue.NO_AR;
    }
    return isYes ? FieldValue.YES : FieldValue.NO;
  }

  getCompanyDetail() {
    let request = {
      "body": {
        "ProcessName": "DC Create Company Profile WF",
        "DCMethod": "GETPROFILE",
        "AccountId": this.primaryAccountId,
        "DC Channel": this.lang == 'ar' ? FieldValue.CHANEL_ONLINE_AR : FieldValue.CHANEL_ONLINE_EN,
      }
    }
    this.api.post(
      ApiRoot.DCCICPIntegration,
      ApiEndPoints.CREATE_UPDATE_SR,
      request
    ).subscribe({
      next: (response: any) => {
        if (response?.SiebelMessage) {
          const profile =
            response?.SiebelMessage?.ListOfDCCompanyProfileIO?.["DC Company Profile BC"] ||
            response?.SiebelMessage?.["DC Company Profile BC"];
          if (!profile) {
            return;
          }
          const prioritiesRaw =
            profile?.["DC Company Business Priorities BC"];
          const priorities = Utils.checkIsArray(prioritiesRaw);
          // .map((item: any) =>
          //   item?.["DC Business Priorities"]
          // ).filter((value: any) => value);

          let prioritiesList: any = [];
          if (priorities) {
            priorities.map((p: any) => {
              prioritiesList.push(p["DC Business Priorities"])
            })
          }

          this.form.patchValue({
            companyName: profile["DC Legal Company Name"] ?? '',
            industry: profile["DC Sector/Industry"] ?? '',
            otherIndustry: profile["DC Other Sector/Industry"] ?? '',
            employees: profile["DC Number of Employees"] ?? '',
            websiteURL: profile["DC Website"] ?? '',
            linkedInURL: profile["DC LinkedIn"] ?? '',
            otherURL: profile["DC Other"] ?? '',
            businessPriorities: prioritiesList,
            communicationChannel: profile["DC Preferred Communication Channel"] ?? '',
            industrySurveys: this.getYesNoValueFromFlag(profile["DC Industry servey Flag"]),
            receivePromotions: this.getYesNoValueFromFlag(profile["DC Pramotion update  Flag"])
          }, { emitEvent: false });

          this.updateOtherIndustryRequirement(this.form.get('industry')?.value);
        }
      },
      error: () => {
        this.technicalErrorMsg = true;
      }
    })
  }

  formSubmitLoader: boolean = false;
  technicalErrorMsg: boolean = false;
  isSaved: boolean = false;
  saveAndClose() {
    if (this.formSubmitLoader) {
      return;
    }
    if (Utils.hasMaxLengthError(this.form)) {
      this.form.markAllAsTouched();
      return;
    }
    if (this.form.get('companyName')?.valid) {
      this.submitData(true);
    }
  }
  submit() {
    if (this.formSubmitLoader) {
      return;
    }
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return;
    }
    this.submitData(true);
  }

  submitData(navigateAfterSubmit: boolean) {
    const formValue = this.form.getRawValue();
    this.requestPayload.body.DCMethod = "SETPROFILE";
    this.requestPayload.body['DC Channel'] = this.lang == 'ar' ? FieldValue.CHANEL_ONLINE_AR : FieldValue.CHANEL_ONLINE_EN,
    this.requestPayload.body.AccountId = this.primaryAccountId;

    this.requestPayload.body.SiebelMessage.MessageId = Utils.generateUniqueId();
    const profile =
      this.requestPayload.body.SiebelMessage.ListOfDCCompanyProfileIO["DC Company Profile BC"];
    profile["DC Legal Company Name"] = formValue.companyName ? formValue.companyName.trim() : "";
    profile["DC Sector/Industry"] = formValue.industry ?? "";
    profile["DC Other Sector/Industry"] = formValue.otherIndustry ? formValue.otherIndustry.trim() : "";
    profile["DC Number of Employees"] = formValue.employees ?? "";
    profile["DC Website"] = formValue.websiteURL ?? "";
    profile["DC LinkedIn"] = formValue.linkedInURL ?? "";
    profile["DC Other"] = formValue.otherURL ?? "";
    profile["DC Preferred Communication Channel"] =
      formValue.communicationChannel ?? "";
    profile["DC Industry servey Flag"] = this.getYesNoFlag(formValue.industrySurveys);
    profile["DC Pramotion update  Flag"] = this.getYesNoFlag(formValue.receivePromotions);

    const priorities = Array.isArray(formValue.businessPriorities)
      ? formValue.businessPriorities
      : formValue.businessPriorities
        ? [formValue.businessPriorities]
        : [];

    profile["ListOfDC Company Business Priorities BC"][
      "DC Company Business Priorities BC"
    ] = priorities.map((priority: string) => ({
      "DC Business Priorities": priority,
      Id: ""
    }));

    if (priorities && priorities.length > 3) {
      this.sharedService.toastMessage('Info', 'MAX_THREE_BUSINESS_PRIORITIES', 'info', true)
      return;
    }
    this.formSubmitLoader = true;
    this.technicalErrorMsg = false;
    this.api.post(
      ApiRoot.DCCICPIntegration,
      ApiEndPoints.CREATE_UPDATE_SR,
      this.requestPayload
    ).subscribe({
      next: (response: any) => {
        if (!response['Error Code'] && !response['Error Message']) {
          this.isSaved = true;
          this.formSubmitLoader = false;
          this.sharedService.toastMessage('SUCCESS', 'SUCCESS', 'success');
          if (navigateAfterSubmit) {
            this.router.navigate([RoutesPath.DASHBOARD]);
          }
        } else {
          this.formSubmitLoader = false;
          this.technicalErrorMsg = true;
        }
      },
      error: () => {
        this.formSubmitLoader = false;
        this.technicalErrorMsg = true;
      }
    })
  }
}
